'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';

import { hasConsent } from 'utils/attribution/consent';
import { isEUVisitor } from 'utils/geo/region';

const RUDDER_WRITE_KEY = '36uEfu39JJIotCCZWmy9A5FgUgx';
const RUDDER_DATAPLANE_URL = 'https://prismaticheahx.dataplane.rudderstack.com';

const CONSENT_UPDATED_EVENT = 'tracking-consent-updated';

// Same interaction-or-fallback loading as LazySegmentScript: first
// interaction triggers immediately, and the short 1s fallback covers fully
// passive non-EU sessions. Events fired earlier are buffered by the
// snippet queue and analyticsClient. The short timer is a business
// decision (no analytics gap for passive visitors) and lands inside the
// PSI/Lighthouse trace window by design.
const NON_EU_FALLBACK_MS = 1000;

const shouldLoadScript = (): boolean => {
  if (isEUVisitor()) {
    return hasConsent('marketingAndAnalytics');
  }

  return true;
};

const isRudderReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const r = (window as any).rudderanalytics;

  if (!r || Array.isArray(r)) {
    return false;
  }

  try {
    const instance = r.getAnalyticsInstance?.();

    return instance?.initialized === true;
  } catch {
    return false;
  }
};

const LazyRudderStackScript = () => {
  const [scriptInjected, setScriptInjected] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const firedPageRef = useRef(false);

  const firePageIfAllowed = useCallback(() => {
    if (firedPageRef.current) {
      return;
    }
    if (!shouldLoadScript()) {
      return;
    }
    if (!isRudderReady()) {
      return;
    }

    firedPageRef.current = true;

    try {
      (window as any).rudderanalytics.page();
    } catch {
      // swallow
    }
  }, []);

  const injectRudderScript = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (scriptInjected || document.getElementById('lazy-rudderstack-script')) {
      return;
    }

    if ((window as any).rudderanalytics?.snippetExecuted) {
      setScriptInjected(true);

      return;
    }

    const existingScript = document.querySelector('script[src*="cdn.rudderlabs.com"]');
    if (existingScript) {
      setScriptInjected(true);

      return;
    }

    const script = document.createElement('script');
    script.id = 'lazy-rudderstack-script';
    script.type = 'text/javascript';
    script.async = true;

    script.innerHTML = `
      !function(){"use strict";
        window.RudderSnippetVersion="3.2.0";
        var e="rudderanalytics";
        window[e]||(window[e]=[]);
        var rudderanalytics=window[e];
        if(Array.isArray(rudderanalytics)&&!rudderanalytics.snippetExecuted){
          rudderanalytics.snippetExecuted=true;
          window.rudderAnalyticsBuildType="legacy";
          var sdkBaseUrl="https://cdn.rudderlabs.com";
          var sdkVersion="v3";
          var sdkFileName="rsa.min.js";
          var scriptLoadingMode="async";
          var r=["setDefaultInstanceKey","load","ready","page","track","identify","alias","group","reset","setAnonymousId","startSession","endSession","consent","addCustomIntegration"];
          for(var n=0;n<r.length;n++){
            var t=r[n];
            rudderanalytics[t]=function(r){
              return function(){
                var n;
                Array.isArray(window[e])
                  ? rudderanalytics.push([r].concat(Array.prototype.slice.call(arguments)))
                  : null===(n=window[e][r])||void 0===n||n.apply(window[e],arguments);
              }
            }(t);
          }
          try{
            new Function('class Test{field=()=>{};test({prop=[]}={}){return prop?(prop?.property??[...prop]):import("");}}');
            window.rudderAnalyticsBuildType="modern";
          }catch(i){}
          var d=document.head||document.getElementsByTagName("head")[0];
          var o=document.body||document.getElementsByTagName("body")[0];
          window.rudderAnalyticsAddScript=function(e,r,n){
            var t=document.createElement("script");
            t.src=e;
            t.setAttribute("data-loader","RS_JS_SDK");
            r&&n&&t.setAttribute(r,n);
            "async"===scriptLoadingMode?t.async=true:"defer"===scriptLoadingMode&&(t.defer=true);
            d?d.insertBefore(t,d.firstChild):o.insertBefore(t,o.firstChild);
          };
          window.rudderAnalyticsMount=function(){
            !function(){
              if("undefined"==typeof globalThis){
                var r=function getGlobal(){return"undefined"!=typeof self?self:"undefined"!=typeof window?window:null}();
                r&&Object.defineProperty(r,"globalThis",{value:r,configurable:true});
              }
            }();
            window.rudderAnalyticsAddScript(
              "".concat(sdkBaseUrl,"/").concat(sdkVersion,"/").concat(window.rudderAnalyticsBuildType,"/").concat(sdkFileName),
              "data-rsa-write-key",
              "${RUDDER_WRITE_KEY}"
            );
          };
          
          if("undefined"==typeof Promise||"undefined"==typeof globalThis) {
            window.rudderAnalyticsAddScript("https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount");
          } else {
            window.rudderAnalyticsMount();
          }
          var loadOptions={};
          rudderanalytics.load("${RUDDER_WRITE_KEY}","${RUDDER_DATAPLANE_URL}",loadOptions);
        }
      }();
    `;

    document.body.appendChild(script);
    setScriptInjected(true);
  }, [scriptInjected]);

  useEffect(() => {
    if (typeof window === 'undefined' || userInteracted) {
      return;
    }

    const handleInteraction = () => {
      setUserInteracted(true);
    };

    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'touchstart',
      'touchmove',
      'scroll',
      'wheel',
      'click',
      'pointerdown',
      'pointermove',
    ];

    events.forEach(event => {
      window.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    const fallbackTimer = isEUVisitor() ? null : window.setTimeout(handleInteraction, NON_EU_FALLBACK_MS);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
      }
    };
  }, [userInteracted]);

  useEffect(() => {
    if (!userInteracted) {
      return;
    }

    if (shouldLoadScript()) {
      injectRudderScript();
    }
  }, [userInteracted, injectRudderScript]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onConsentUpdated = () => {
      if (shouldLoadScript() && !scriptInjected && userInteracted) {
        injectRudderScript();
      }

      const checkInterval = setInterval(() => {
        if (isRudderReady()) {
          clearInterval(checkInterval);
          firePageIfAllowed();
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 10000);
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);

    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    };
  }, [firePageIfAllowed, scriptInjected, userInteracted, injectRudderScript]);

  useEffect(() => {
    if (typeof window === 'undefined' || !scriptInjected) {
      return;
    }

    if (shouldLoadScript()) {
      const checkInterval = setInterval(() => {
        if (isRudderReady()) {
          clearInterval(checkInterval);
          firePageIfAllowed();
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 10000);

      return () => clearInterval(checkInterval);
    }
  }, [firePageIfAllowed, scriptInjected]);

  return null;
};

export default LazyRudderStackScript;
